<template>
	<li
		:id="`location-card-${location.mmsClubId}`"
		class="result-card border-bottom mx-n2 mx-md-0 p-2"
		:class="{ selected: isSelected }"
		tabindex="0"
		:data-testid="`location-card-${location.mmsClubId}`"
	>
		<div class="image-container position-relative">
			<div
				v-if="location.distance !== undefined && location.distance !== null"
				class="badge badge-glass badge-glass-sm badge-glass-dark badge-distance rounded-pill position-absolute"
				aria-label="distance to location"
			>
				<svg
					xmlns="http://www.w3.org/2000/svg"
					width="16"
					height="16"
					viewBox="0 0 16 16"
					fill="currentColor"
					aria-hidden="true"
				>
					<path
						d="M3.14604 6.97758C2.3706 7.33721 2.58413 8.37674 3.43823 8.38236L7.53456 8.39922C7.60199 8.39922 7.61885 8.41608 7.61885 8.48353L7.63009 12.5461C7.63571 13.434 8.69211 13.5913 9.06859 12.7765L13.2211 3.84774C13.6032 3.01611 12.9514 2.42611 12.1254 2.81383L3.14604 6.97758ZM4.12376 7.50578C4.10129 7.50578 4.09567 7.4833 4.12376 7.47206L12.1984 3.77469C12.2378 3.75783 12.2546 3.76908 12.2378 3.81403L8.51788 11.8831C8.5123 11.9055 8.4898 11.8999 8.4898 11.8774L8.51788 7.90474C8.51788 7.6575 8.34371 7.4833 8.09085 7.4833L4.12376 7.50578Z"
						fill="#FBF7F2"
					/>
				</svg>
				{{ location.distance }} mi
			</div>

			<div
				v-if="location.locationMessage"
				class="badge badge-glass badge-glass-sm badge-glass-dark badge-location-message rounded-pill position-absolute"
				data-testid="location-message"
			>
				{{ location.locationMessage }}
			</div>

			<div
				v-if="location.comingSoon"
				class="badge badge-glass badge-glass-sm badge-glass-dark badge-coming-soon rounded-pill position-absolute mb-1 mb-md-0"
				data-testid="coming-soon"
			>
				{{ location.comingSoon }}
			</div>

			<img
				v-if="smImage"
				:alt="smImage.alt ?? `${location.clubTitle} Exterior`"
				data-testid="thumbnail-sm"
				:src="smImage.path"
				class="result-image rounded-3 mb-1 mb-md-0"
			/>
		</div>

		<div
			class="result-body flex-grow-1 d-flex flex-column justify-content-between overflow-x-hidden"
		>
			<div>
				<div class="result-title h3 mb-1">{{ location.clubTitle }}</div>
				<div
					v-if="location.distance !== undefined && location.distance !== null"
					class="location-distance small opacity-75"
				>
					( {{ location.distance }} mi )
				</div>
				<div
					v-if="location.comingSoon && isMobileMapView"
					class="d-inline-block d-lg-none mt-1"
					data-testid="coming-soon"
				>
					<span class="badge rounded-pill rounded-pill-sm border">{{ location.comingSoon }}</span>
				</div>
				<div
					v-if="location.locationMessage && isMobileMapView"
					class="d-inline-block d-lg-none mt-1"
					data-testid="location-message"
				>
					<span class="badge rounded-pill rounded-pill-sm border">
						{{ location.locationMessage }}
					</span>
				</div>

				<ul
					v-if="location.labels"
					class="amenities-list list-unstyled me-1 mb-1"
					data-testid="amenities-list"
				>
					<li
						v-for="(label, key) in location.labels"
						:key="key"
						class="amenity d-inline-block me-1 mb-1 p-0"
					>
						<span class="badge rounded-pill rounded-pill-sm border d-flex gap-1 w-max-content">
							<template v-if="label.iconId">
								<svg class="lti" width="1em" height="1em" fill="currentColor" aria-hidden="true">
									<use
										:href="`/etc/designs/ltnext/hanko-svg/life-time-icons.svg#${label.iconId}`"
									/>
								</svg>
							</template>
							{{ label.name }}
						</span>
					</li>
				</ul>
			</div>

			<div class="d-flex align-items-baseline">
				<div v-if="link && !hideStartingAtPriceLink" class="small opacity-75">
					<span
						v-if="!startingAtPriceLinkOverrideText && !isWebPreSaleWaitlistClub"
						class="d-inline-block me-1"
						data-testid="startAtPrice"
					>
						<span class="visually-hidden">{{ location.clubTitle }}</span>
						Starting at {{ location.startingPriceString }}<span aria-hidden="true">/mo</span>
						<span class="visually-hidden">per month</span>
						<a
							v-if="showSurchargeDisclaimer"
							class="disclaimer-link"
							href="#surcharge-disclaimer"
							aria-label="Surcharge disclaimer"
							aria-describedby="surcharge-disclaimer"
							data-testid="disclaimer-link"
						>
							&ast;
						</a>
					</span>
					<a
						:id="joinId"
						:href="link"
						data-testid="join-link"
						class="join-link"
						@click.prevent="trackJoin(link, interactions)"
					>
						{{ ctaText }}
						<span class="visually-hidden">at {{ location.clubTitle }}</span>
					</a>
				</div>
				<a
					v-if="location.prospectPagePath"
					:id="detailsId"
					:href="location.prospectPagePath"
					class="btn btn-dark btn-sm px-2 px-sm-3 px-lg-2 px-xl-3 ms-auto"
					data-testid="details-link"
					@click.prevent="trackDetails(location.prospectPagePath, interactions)"
				>
					Details <span class="visually-hidden">about {{ location.clubTitle }}</span>
				</a>
			</div>
		</div>
	</li>
</template>

<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useAdobeDataLayer } from '@/composables/use-adobe-data-layers';
import { useD360MapFiltersStore } from '@/stores/map-d360-filters';
import {
	findImage,
	getStartingAtPriceLinkOverrideText,
	joinLink,
	joinLinkText,
	isWebPreSaleWaitlist,
	showSurchargeDisclaimer as showSurchargeUtil
} from '@/util/location';
import type { Location } from 'Types/aemui/find-a-location/findaLocation';

const props = defineProps<{
	location: Location;
	isSelected: boolean;
	isMobileMapView: boolean;
}>();

const { hideStartingAtPriceLink } = props.location;

const smImage = computed(() => findImage(props.location, '16x9'));

const startingAtPriceLinkOverrideText = computed(() =>
	getStartingAtPriceLinkOverrideText(props.location)
);

const link = computed(() => joinLink(props.location));
const ctaText = computed(() => joinLinkText(props.location));
const isWebPreSaleWaitlistClub = computed(() => isWebPreSaleWaitlist(props.location));

const showSurchargeDisclaimer = computed(() => showSurchargeUtil(props.location));

const d360MapStore = useD360MapFiltersStore();
const { selectedFilters } = storeToRefs(d360MapStore);

const interactions = computed(() => {
	const selected = selectedFilters.value.map((filter) => filter.name);
	return {
		action: 'filter-applied',
		filters: { selected }
	};
});

const { componentId: detailsId, trackLink: trackDetails } = useAdobeDataLayer({
	name: 'findlocation',
	type: 'link',
	title: 'Details',
	link: props.location.prospectPagePath
});

const { componentId: joinId, trackLink: trackJoin } = useAdobeDataLayer({
	name: 'findlocation',
	type: 'link',
	title: `Starting at ${props.location.startingPriceString}/mo`,
	link: joinLink(props.location)
});

defineExpose({
	trackDetails,
	trackJoin
});
</script>

<style lang="scss" scoped>
.disclaimer-link {
	display: inline-block;
	height: 24px;
}

.result-card {
	.image-container {
		.badge {
			display: block;
			line-height: 16px;

			&-distance {
				top: 12px;
				left: 12px;
				max-width: calc(50% - 1rem);
			}

			&-location-message {
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
				top: 12px;
				right: 12px;
				max-width: calc(100% - 5rem);

				@include media-breakpoint-up(md) {
					max-width: calc(50% + 1rem);
				}
			}

			&-coming-soon {
				bottom: 12px;
				left: 12px;
			}
		}
	}

	.location-distance {
		display: none;
	}

	.join-link {
		text-underline-offset: 0.3em;
	}
}

body:not(.find-location-mapview) {
	.result-card {
		@include media-breakpoint-up(md) {
			display: flex;
			gap: 1rem;
		}

		.image-container {
			@include media-breakpoint-up(md) {
				height: 9.5rem;
			}
		}
	}
}

.find-location-mapview {
	.result-card {
		@include media-breakpoint-up(lg) {
			display: flex;
			gap: 1rem;
		}

		.image-container {
			@include media-breakpoint-up(lg) {
				height: 9.5rem;
			}

			.badge {
				display: none;

				@include media-breakpoint-up(lg) {
					display: block;
				}
			}
		}

		.location-distance {
			@include media-breakpoint-down(lg) {
				display: block;
			}
		}

		.amenities-list {
			// show amenities list everywhere except mobile map view
			display: none;

			// default behavior is to always show amenities as a wrapping list (when shown)
			@include media-breakpoint-up(lg) {
				display: inline-block;
				overflow-x: auto;
				scroll-snap-type: none;
			}
		}
	}
}

html[data-whatinput='touch'] {
	// if the user has a touch input device, make the amenities list swipe-able
	body:not(.find-location-mapview) {
		.amenities-list {
			display: flex;
			flex-wrap: nowrap;
			scroll-snap-type: x mandatory;
			overflow-x: scroll;

			.amenity {
				scroll-snap-align: start;
			}

			&::-webkit-scrollbar {
				display: none;
			}
		}
	}

	.find-location-mapview {
		.result-card {
			.amenities-list {
				@include media-breakpoint-up(lg) {
					display: flex;
					flex-wrap: nowrap;
					scroll-snap-type: x mandatory;
					overflow-x: scroll;

					.amenity {
						scroll-snap-align: start;
					}

					&::-webkit-scrollbar {
						display: none;
					}
				}
			}
		}
	}
}
</style>
